import { ref, inject, computed } from 'vue';
import { useStore } from 'vuex';
import { I18nInjectionKey } from 'vue-i18n';

import { showConfirmCustom } from '@/commons/globalMessage';

// Enums
import { tabIdEnum } from '@/components/SelectUser/selectUserEnum.js';
import { TaskAction, TaskStatus } from '@/commons/wildcards/taskEnum.js';
import { MSEnum } from '@/commons/enumeration.js';
import { settingEnum } from "@/views/setting/settingEnum.js";
import { TaskApprovalEnum } from '@/components/popup/popup-add-task/taskApprovalEnum.js';

// Apis
import taskAPI from '@/apis/eTask/taskAPI.js';
import organizationUnitAPI from '@/apis/eTask/organizationUnitAPI.js';
import userAPI from '@/apis/eTask/userAPI.js';
import groupAPI from '@/apis/system/groupAPI';
import generalSettingAPI from '@/apis/eTask/generalSettingAPI.js';
import authorizedPersonAPI from '@/apis/system/authorizedPersonAPI.js';

/**
 * Hàm lấy danh sách người chủ trì, phối hợp
 * @param {object} payload Gồm skip, take, filter, sort
 * @param {boolean} isGetPeopleInvolved Có phải là lấy người liên quan ko
 * @param {object} parentTask Dữ liệu công việc cha dùng để thêm người chủ trì và người phối hợp vào danh sách nếu ko có thì ko thêm
 * @author: BMThang(19/12/2022)
 */
export const queryFuncGetUsers = (payload, isGetPeopleInvolved = false, parentTask = null, currentTask = null) => {
  const { skip, take, filter, sort } = payload;
  let executorsAndCombinations = [];
  const newFilter = JSON.parse(filter || '[]');
  // Nếu truyền công việc cha và cha có người chủ trì thì ko lấy người chủ trì từ server mà lấy từ công việc cha luôn
  // Và ko phải lấy người liên quan
  if (parentTask?.Executor?.length && !isGetPeopleInvolved) {
    newFilter.push({
      Field: 'UserID',
      Opeator: 'NOT IN',
      Value: parentTask.Executor.map(u => u.UserID),
    });
    // Chỉ thêm vào danh sách khi skip = 0 (Lần tải dữ liệu đầu tiên)
    if (skip === 0) {
      executorsAndCombinations.push(...parentTask.Executor);
    }
  }
  // Nếu truyền công việc cha và cha có người phối hợp thì ko lấy người phối hợp từ server mà lấy từ công việc cha luôn
  // Và ko phải lấy người liên quan
  if (parentTask?.Combinations?.length && !isGetPeopleInvolved) {
    newFilter.push({
      Field: 'UserID',
      Opeator: 'NOT IN',
      Value: parentTask.Combinations.map(u => u.UserID),
    })
    // Chỉ thêm vào danh sách khi skip = 0 (Lần tải dữ liệu đầu tiên)
    if (skip === 0) {
      executorsAndCombinations.push(...parentTask.Combinations);
    }
  }

  //Nếu lấy danh sách người liên quan, người phụ trách thì bỏ người giao việc ra
  if (isGetPeopleInvolved && currentTask?.Assigner?.length) {
    newFilter.push({
      Field: 'UserID',
      Opeator: '!=',
      Value: currentTask.Assigner[0].UserID,
    })
  }

  const newFilterString = JSON.stringify(newFilter);
  const listQuery = [
    userAPI.getPagingUserWithAuth(skip, take, newFilterString, sort, isGetPeopleInvolved),
    userAPI.getPagingUserWithAuthSummary(newFilterString, isGetPeopleInvolved),
  ]

  // Nếu có người chủ trì và người phối hợp thì lấy những người này truyền thẳng vào danh sách người dùng
  if (executorsAndCombinations.length) {
    const filterExecutor = [];

    // Lấy executor và combination
    filterExecutor.push({
      Field: 'UserID',
      Opeator: 'IN',
      Value: executorsAndCombinations.map(user => user.UserID),
    })

    // Filter Tên
    const filterName = newFilter.find(currentFilter => currentFilter.Field === 'FullName')
    if (filterName) {
      filterExecutor.push({
        Field: 'FullName',
        Opeator: '*',
        Value: filterName.Value,
      });
    }

    // Filter đơn vị
    const filterOrg = newFilter.find(currentFilter => currentFilter.Field === 'MISACode')
    if (filterOrg) {
      filterExecutor.push({
        Field: 'MISACode',
        Opeator: '.*',
        Value: filterOrg.Value,
      });
    }

    // Filter phòng ban
    const filterDepartment = newFilter.find(currentFilter => currentFilter.Field === 'OrganizationUnitID')
    if (filterDepartment) {
      filterExecutor.push({
        Field: 'OrganizationUnitID',
        Opeator: '=',
        Value: filterDepartment.Value,
      });
    }

    const filterExecutorString = JSON.stringify(filterExecutor);

    // Push vào danh sách query
    listQuery.push(userAPI.getPaging({
      skip: 0,
      take: -1,
      sort: 'MISACode,RoleType,FullName',
      filter: filterExecutorString,
      columns: 'UserID: user.UserID,UserName,MISAID,MISAIDEmail,DateOfBirth,FirstName,LastName,Email,FullName,Mobile,WorkPhone,OrganizationUnitID,OrganizationUnitName,OrganizationID,OrganizationName,Status,JobTitleID,JobTitleName,RoleType'
    }));
  }
  return new Promise((resolve, reject) => {
    Promise.all(listQuery)
      .then(res => {
        if (res.length >= 2) {
          const userListRes = res[0];
          const userSummaryRes = res[1];
          const executorsAndCombinationsRes = res.length === 3 ? res[2].Data : [];

          resolve({
            Data: [...executorsAndCombinationsRes, ...userListRes.Data],
            Total: executorsAndCombinationsRes.length + userSummaryRes.Total,
          });
        } else {
          reject('error');
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
}

/**
 * Hàm lấy danh sách đơn vị
 * @param {object} payload Gồm organizationUnitID, searchText
 * @param {null|number} userRoleType Vai trò người dùng
 * @author: BMThang(19/12/2022)
 */
export const queryFuncGetOrgs = (payload, userRoleType = null) => {
  const { organizationUnitID, searchText, isGetParentAndChildren } = payload;

  return new Promise((resolve, reject) => {
    // Nếu là chuyên viên thì trả về mảng rỗng luôn
    if (userRoleType && userRoleType === MSEnum.UserRoleType.Employee) {
      return resolve({
        Data: [],
      });
    }

    organizationUnitAPI.getPagingTree(organizationUnitID, searchText, searchText, true, false, false, isGetParentAndChildren)
      .then(res => {
        resolve({
          Data: res,
        });
      })
      .catch((err) => {
        reject(err);
      });
  });
}

/**
 * Lấy danh sách nhóm người dùng
 * @param {object} payload gồm searchText
 * @param {string} ownerId id người tạo nhóm
 * @author: BMThang(28/12/2022)
 */
export const queryFuncGetUserGroups = (payload, ownerId = "") => {
  const param = {
    skip: 0,
    take: -1,
    sort: "SortOrder, Created DESC",
    filter: `[{Field: "GroupName", Opeator: "*", Value:"${payload.searchText || ""}"}, {Field: "OwnerID", Opeator: "=", Value: "${ownerId}"}]`,
    emptyFilter: "",
    columns: 'GroupID, GroupName, Description, TotalMember, OwnerID'
  }
  return new Promise((resolve, reject) => {
    groupAPI.getListGroup(param)
      .then(res => {
        resolve({
          Data: res.Data,
        });
      })
      .catch((err) => {
        reject(err);
      });
  })
}

/**
 * Thêm đối tượng bị xóa hoặc thêm mới vào mảng kết quả
 * @param {array} newData mảng mới
 * @param {array} newData mảng cũ
 * @param {string} idField thuộc tính id
 * @returns {array} mảng sau khi thêm các đối tượng bị xóa và thêm mới
 * @author: BMThang(03/01/2023)
 */
export const insertDeletedEntitiesAndAddedEntities = (newData = [], oldData = [], idField = null) => {
  if (!idField) {
    return [];
  }
  let result = [];
  const deletedEntities = oldData.filter(entity => {
    // Ko lấy các entity duplicate
    if (entity.State === MSEnum.ModelState.Duplicate) {
      return false;
    }
    return newData.findIndex(e => e[idField] === entity[idField]) === -1;
  });
  const formattedDeleteEntities = deletedEntities.map(entity => ({ ...entity, State: MSEnum.ModelState.Delete }));
  newData.forEach(entity => {
    // Nếu là duplicate thì ko cần đổi trạng thái
    if (entity.State === MSEnum.ModelState.Duplicate) {
      return;
    }

    // Nếu ko tìm thầy trong oldData thì là thêm mới
    if (oldData.findIndex(e => e[idField] === entity[idField]) === -1) {
      entity.State = MSEnum.ModelState.Insert;
    }
  })
  result = [...newData, ...formattedDeleteEntities];
  return result;
}

/**
 * Thêm người dùng bị xóa hoặc thêm mới vào mảng kết quả
 * @param {array} newData mảng mới
 * @param {array} newData mảng cũ
 * @returns {array} mảng sau khi thêm các người dùng bị xóa và thêm mới
 * @author: BMThang(23/12/2022)
 */
const insertDeletedUsersAndAddedUsers = (newData = [], oldData = []) => {
  let result = [];
  // NOTE: Filter theo ID, FullName, OrganizationUnitID, OrganizationUnitName để khi đổi tên rồi chọn lại thì sẽ lấy tên mới
  const deletedEntities = oldData.filter(entity => {
    return newData.findIndex(e =>
      e.UserID === entity.UserID &&
      e.FullName === entity.FullName &&
      e.OrganizationUnitID === entity.OrganizationUnitID &&
      e.OrganizationUnitName === entity.OrganizationUnitName
    ) === -1;
  });
  const formattedDeleteEntities = deletedEntities.map(entity => ({ ...entity, State: MSEnum.ModelState.Delete }));
  newData.forEach(entity => {
    // Nếu ko tìm thầy trong oldData thì là thêm mới
    if (oldData.findIndex(e =>
      e.UserID === entity.UserID &&
      e.FullName === entity.FullName &&
      e.OrganizationUnitID === entity.OrganizationUnitID &&
      e.OrganizationUnitName === entity.OrganizationUnitName
    ) === -1) {
      entity.State = MSEnum.ModelState.Insert;
    }
  })
  result = [...newData, ...formattedDeleteEntities];
  return result;
}

/**
 * Format hạn hoàn thành trước khi lưu
 * @param {Date|string|null} endDate Hạn hoàn thành
 * @param {bool} isSelectEndTime Có chọn thời gian hoàn thành ko
 * @returns Hạn hoàn thành đc format
 * @author: BMThang(12/01/2022)
 */
const formatEndDateBeforeSave = (endDate, isSelectEndTime) => {
  if (!endDate) {
    return null;
  }
  let formattedDate = endDate;

  // Nếu ko chọn thời gian thì xét giá trị mặc định là 23:59:59
  if (!isSelectEndTime) {
    if (!(formattedDate instanceof Date)) {
      formattedDate = new Date(formattedDate);
    }
    formattedDate.setHours(23);
    formattedDate.setMinutes(59);
    formattedDate.setSeconds(59);
  }
  return formattedDate;
}

/**
 * Format công việc trước khi lưu
 * @param {object} task công việc cần format
 * @param {number} taskStatus trạng thái công việc
 * @author: BMThang(23/12/2022)
 */
const formatTaskBeforeSave = (task, oldTask, taskStatus = TaskStatus.Draft) => {
  //Format đề xuất hạn hoàn thành
  if (task.TaskUpdateDeadline) {
    task.TaskUpdateDeadline.Approver = JSON.stringify(task.TaskUpdateDeadline.Approver[0]);
  }

  // Format tệp đính kèm
  if (task.Attachments.length === 0) {
    task.Attachments = null;
  }

  // Format tệp kết quả công việc
  if (task.AttachmentResults.length === 0) {
    task.AttachmentResults = null;
  }
  else {
    task.AttachmentResults = insertDeletedEntitiesAndAddedEntities(task.AttachmentResults, oldTask.AttachmentResults, 'FileID');
  }

  // Format người phối hợp
  if (task.Combinations.length === 0) {
    task.CombinationIDs = null;
    task.Combinations = [];
  } else {
    task.CombinationIDs = task.Combinations.map(c => {
      return c.UserID;
    }).join(',');
  }
  task.Combinations = insertDeletedUsersAndAddedUsers(task.Combinations, oldTask.Combinations);

  // Format người liên quan
  if (task.PeopleInvolveds.length === 0) {
    task.PeopleInvolvedIDs = null;
    task.PeopleInvolveds = [];
  } else {
    task.PeopleInvolvedIDs = task.PeopleInvolveds.map(c => {
      return c.UserID;
    }).join(',');
  }
  task.PeopleInvolveds = insertDeletedUsersAndAddedUsers(task.PeopleInvolveds, oldTask.PeopleInvolveds);

  // Format người chủ trì
  if (task.Executor?.length) {
    task.ExecutorID = task.Executor[0].UserID;
  }

  // Format người phụ trách
  if (task.Supervisor?.length > 0) {
    task.SupervisorID = task.Supervisor[0].UserID;
    task.Supervisor = task.Supervisor[0];
  } else {
    task.SupervisorID = null;
    task.Supervisor = null;
  }

  // Format người giao việc
  task.AssignerID = task.Assigner[0].UserID;
  task.Assigner = task.Assigner[0];

  // Format mức độ ưu tiên
  task.Priority = task.Priority[0];

  // Format loại việc
  task.TaskType = task.TaskType[0];

  // Format trạng thái
  task.Status = taskStatus;

  // Format hạn hoàn thành
  task.EndDate = formatEndDateBeforeSave(task.EndDate, task.IsSelectEndTime);
  task.StartDate = formatEndDateBeforeSave(task.StartDate, task.IsSelectStartTime);

  // Format ngày gửi (ngày giao)
  // Nếu ko có ngày giao và trạng thái là đang xử lý thì xét ngày giao
  if (!task.SendTaskDate && task.Status === TaskStatus.InProcess) {
    task.SendTaskDate = new Date();
  }

  // Format công việc con
  task.ChildrenTask = task.ChildrenTask?.length > 0 ? task.ChildrenTask : null;

  // Gọi đệ quy
  task.ChildrenTask?.forEach((childTask, idx) => {
    // NOTE: Cần xóa task con trong oldTask khi xóa task con

    // Công việc con cũ mặc định nếu công việc này đc thêm mới ko có trong cha
    let oldChildTask = {
      Combinations: [],
      PeopleInvolveds: [],
      Attachments: [],
      ChildrenTask: [],
    };
    // Nếu có công việc con cũ trong cha thì lấy thông tin đó
    if (idx < oldTask.ChildrenTask.length) {
      oldChildTask = oldTask.ChildrenTask[idx];
    }
    formatTaskBeforeSave(childTask, oldChildTask, childTask.Status);
  })
}

/**
 * Lấy danh sách option
 * @param {number} settingType loại setting
 * @returns danh sách option
 * @author: BMThang(23/12/2022)
 */
export const getSettingOptions = async (settingType) => {
  try {
    const res = await generalSettingAPI.getGeneralSettingByType(settingType);
    return res.length ? res : [];
  } catch (e) {
    console.log(e)
    return [];
  }
}

/**
 * Xét lại giá trị công việc sau khi lưu
 * @param {object} oldTaskData Công việc hiện tại
 * @param {array} savedTasks Danh sách kêt quả lưu công việc
 * @author: BMThang(12/01/2022)
 */
export const setTaskDataAfterSave = (oldTaskData, savedTasks, createdBy = '') => {
  let childrenTask = [oldTaskData];
  while (childrenTask.length > 0) {
    const taskCount = childrenTask.length;
    const newChildrenTask = [];
    for (let index = 0; index < taskCount; index++) {
      const task = childrenTask[index];
      const savedTask = savedTasks[0];
      task.Status = savedTask.Status;
      task.EditVersion = savedTask.EditVersion;
      task.Modified = savedTask.Modified;
      task.TaskID = savedTask.TaskID;
      task.Created = savedTask.Modified;
      task.CreatedBy = createdBy;
      task.State = MSEnum.ModelState.None;
      task.IsNew = false;
      savedTasks.shift();
      if (task.ChildrenTask.length) {
        // Chỉ lấy những công việc đc thêm mới hoặc cập nhật
        const updatedChildrentTask = task.ChildrenTask.filter(t =>
          t.State === MSEnum.ModelState.Insert ||
          t.State === MSEnum.ModelState.Update
        )
        newChildrenTask.push(...updatedChildrentTask);
      }
    }
    childrenTask = newChildrenTask;
  }
}

/**
 * Định dạng danh sách công việc đc lưu.
 * Chuyển trạng thái (Status, State)
 * Lấy TaskID.
 * Nếu gửi hàng loạt thì tạo nhiều công việc tương tự
 * @param {array} saveResult kết quả sau khi lưu dạng pagingResult: {Data: [{TaskID: 1, Status: 1}]}
 * @param {object} savedTaskData dữ liệu của công việc đc lưu
 * @returns {array} Danh sách công việc đc định dạng
 */
export const formatSavedTasks = (saveResult, savedTaskData, createdBy = '') => {
  const clonedSavedTask = JSON.parse(JSON.stringify(savedTaskData));
  let formattedSaveResult = [];
  if (saveResult.Data.length) {
    formattedSaveResult = saveResult.Data;
  }
  const resultTasks = [];

  // Nếu ko phải lưu nháp thì clone thêm các công việc khác dựa theo số lượng người thực hiện
  if (savedTaskData.Status !== TaskStatus.Draft) {
    clonedSavedTask.Executor.forEach((executor, idx) => {
      let taskStatus = TaskStatus.None;
      let taskID = null;
      let editVersion = 0;
      let attachmentResults = null;
      if (idx < formattedSaveResult.length) {
        taskStatus = formattedSaveResult[idx].Status;
        taskID = formattedSaveResult[idx].TaskID;
        editVersion = formattedSaveResult[idx].EditVersion;
        let attachmentResultsJson = null;
        if (formattedSaveResult[idx].AttachmentResults) {
          attachmentResultsJson = JSON.parse(formattedSaveResult[idx].AttachmentResults);
        }
        attachmentResults = attachmentResultsJson;
      }
      resultTasks.push({
        ...clonedSavedTask,
        State: MSEnum.ModelState.None,
        Status: taskStatus,
        TaskID: taskID,
        Executor: [executor],
        EditVersion: editVersion,
        IsNew: false,
        AttachmentResults: attachmentResults,
      })
    })
  }
  // Nếu là lưu nháp thì chỉ lấy task đầu tiên
  else {
    let taskStatus = TaskStatus.None;
    let taskID = null;
    let editVersion = 0;
    let attachmentResults = null;
    if (formattedSaveResult.length) {
      taskStatus = formattedSaveResult[0].Status;
      taskID = formattedSaveResult[0].TaskID;
      editVersion = formattedSaveResult[0].EditVersion;
      let attachmentResultsJson = null;
      if (formattedSaveResult[0].AttachmentResults) {
        attachmentResultsJson = JSON.parse(formattedSaveResult[0].AttachmentResults);
      }
      attachmentResults = attachmentResultsJson;
    }
    resultTasks.push({
      ...clonedSavedTask,
      State: MSEnum.ModelState.None,
      Status: taskStatus,
      TaskID: taskID,
      EditVersion: editVersion,
      IsNew: false,
      AttachmentResults: attachmentResults,
    });
  }

  const resultTasksCount = resultTasks.length;
  for (let index = 0; index < resultTasksCount; index++) {
    const resultTask = resultTasks[index];
    setTaskDataAfterSave(resultTask, formattedSaveResult, createdBy)
  }

  return resultTasks;
}

/**
 * Kiểm tra các công việc con đã valid chưa
 * @param {object} parentTask Công việc cha
 * @param {boolean} isCheckExecutor Có kiểm tra người chủ trì ko
 * @param {boolean} isCheckEndDate Có kiểm tra hạn hoàn thành ko
 * @returns true nếu valid. False nếu invalid
 * @author: BMThang(29/12/2022)
 */
export const checkValidateChildrenTask = (parentTask, isCheckExecutor = false, isCheckEndDate = false) => {
  const tasks = parentTask.ChildrenTask;
  const taskCount = tasks.length;
  let errorMsg = '';
  for (let index = 0; index < taskCount; index++) {
    const task = tasks[index];
    if (isCheckExecutor && !task.Executor.length) {
      errorMsg = 'executor';
      break;
    }
    if (!task.TaskName) {
      errorMsg = 'name';
      break;
    }
    if (isCheckEndDate && parentTask.EndDate && task.EndDate && (parentTask.EndDate < task.EndDate)) {
      errorMsg = 'endDate';
      break;
    }
    errorMsg = checkValidateChildrenTask(task);
    if (errorMsg) {
      break;
    }
  }
  return errorMsg;
}

/**
 * Có ẩn hay hiện control ko
 * @param {number} value giá trị để kiểm tra
 * @returns Có hiện control ko
 * @author: BMThang(03/01/2022)
 */
export const isShowControl = (value) => {
  if (value === window.TaskRolesValue.Hidden) {
    return false;
  }
  return true;
}

/**
 * Có disabled control ko
 * @param {number} value giá trị để kiểm tra
 * @returns Có disabled control ko
 * @author: BMThang(03/01/2022)
 */
export const isDisabledControl = (value) => {
  if (value === window.TaskRolesValue.ByRole || value === window.TaskRolesValue.All) {
    return false;
  }
  return true;
}

/**
 * Có đc toàn quyền sử dụng control ko (Có thể dùng đc cả dữ liệu của người khác)
 * @param {number} value giá trị để kiểm tra
 * @returns Có đc toàn quyền sử dụng control ko
 * @author: BMThang(03/01/2022)
 */
export const isAdminControl = (value) => {
  if (value === window.TaskRolesValue.All) {
    return true;
  }
  return false;
}

/**
 * Task composition
 * @param {bool} isGetSetting Có gọi API lấy dữ liệu setting ko (loại công việc, người ủy quyền, mức độ ưu tiên)
 * @param {object[]} inputTaskTypeOptions Các option loại công việc mặc định
 * @param {object[]} inputPriorityOptions Các option mức độ ưu tiên mặc định
 * @author: BMThang(14/01/20223)
 */
export const useTask = (isGetSetting = false, inputTaskTypeOptions = [], inputPriorityOptions = [], parentTask = null) => {
  const { t } = inject(I18nInjectionKey);
  const toast = inject('$toast');
  const store = useStore();

  /**
   * Các option loại công việc chưa format
   */
  const taskTypeOptionsRaw = ref([...inputTaskTypeOptions]);

  /**
   * Các option mức độ ưu tiên chưa format
   */
  const priorityOptionsRaw = ref([...inputPriorityOptions]);

  /**
   * Những người ủy quyền cho mình chưa format
   */
  const authorizedPersionRaw = ref([]);

  /**
   * Thời gian quá hạn chưa format
   */
  const deadlineRaw = ref([]);

  /**
   * Danh sách tab chọn người chủ trì
   */
  const tabListOwner = ref([
    {
      id: tabIdEnum.SelectUserTab,
      name: t('i18nCommon.Personal'),
      queryFunction: (payload) => queryFuncGetUsers(payload, false, parentTask),
    },
    {
      id: tabIdEnum.SelectOrgTab,
      name: t('i18nCommon.OrganizationUnit'),
      queryFunction: (payload) => queryFuncGetOrgs(payload, currentUserByAuthorization.value.RoleType),
    },
    {
      id: tabIdEnum.SelectGroupTab,
      name: t('i18nCommon.WorkGroup'),
      queryFunction: (payload) => queryFuncGetUserGroups(payload, store.state.context.User.UserID),
      isShowWhenMultiple: true,
    },
  ]);

  /**
   * Danh sách tab chọn người phối hợp
   */
  const tabListCombination = ref([
    {
      id: tabIdEnum.SelectUserTab,
      name: t('i18nCommon.Personal'),
      queryFunction: (payload) => queryFuncGetUsers(payload, false, parentTask),
    },
    {
      id: tabIdEnum.SelectOrgTab,
      name: t('i18nCommon.OrganizationUnit'),
      queryFunction: (payload) => queryFuncGetOrgs(payload, currentUserByAuthorization.value.RoleType),
    },
    {
      id: tabIdEnum.SelectGroupTab,
      name: t('i18nCommon.WorkGroup'),
      queryFunction: (payload) => queryFuncGetUserGroups(payload, store.state.context.User.UserID),
    },
  ]);

  /**
   * Danh sách tab chọn người liên quan
   */
  const tabListPeopleInvolved = ref([
    {
      id: tabIdEnum.SelectUserTab,
      name: t('i18nCommon.Personal'),
      queryFunction: (payload) => queryFuncGetUsers(payload, true, null, taskData.value),
    },
  ]);

  /**
   * Danh sách tab chọn người phê duyệt
   */
  const tabListSupervisor = ref([
    {
      id: tabIdEnum.SelectUserTab,
      name: t('i18nCommon.Personal'),
      queryFunction: (payload) => queryFuncGetUsers(payload, true, null, taskData.value),
    },
  ]);

  /**
   * Dữ liệu task
   */
  const taskData = ref({
    TaskID: null,
    Priority: [],
    TaskName: '',
    DocumentCode: null,
    DocumentDate: null,
    DocumentSigner: '',
    TaskType: [],
    Assigner: [
      { ...store.state.context.User, Created: null, Modified: null },
    ],
    Description: '',
    Executor: [],
    Combinations: [],
    PeopleInvolveds: [],
    EndDate: null,
    StartDate: null,
    IsSelectEndTime: false,
    IsSelectStartTime: false,
    Attachments: [],
    AttachmentResults: [],
    Status: TaskStatus.Draft,
    ParentID: null,
    ParentName: null,
    CreatorID: store.state.context.User.UserID,
    State: MSEnum.ModelState.Insert,
    EditVersion: 0,
    Modified: null,
    ModifiedBy: '',
    Created: null,
    CreatedBy: '',
    ChildrenTask: [],
    Reminder: null,
    Supervisor: [],
  });

  /**
   * Dữ liệu công việc cũ
   */
  const oldTaskData = ref({
    Combinations: [],
    PeopleInvolveds: [],
    Attachments: [],
    ChildrenTask: [],
    FormattedDocument: null,
  });

  /**
   * Phê duyệt công việc
   */
  const taskApprovals = ref([]);

  /**
   * Danh sách mức độ ưu tiên
   */
  const priorityOptions = computed(() => {
    return priorityOptionsRaw.value.map(option => {
      const optionValue = JSON.parse(option.SettingValue);
      return {
        ...option,
        PriorityName: optionValue.OptionValue,
        PriorityColor: optionValue.OptionName,
      }
    });
  })

  /**
   * Danh sách loại công việc
   */
  const taskTypeOptions = computed(() => {
    return taskTypeOptionsRaw.value.map(option => {
      const optionValue = JSON.parse(option.SettingValue);
      return {
        ...option,
        TaskTypeName: optionValue.OptionValue,
      }
    });
  })

  /**
   * Số ngày quá hạn
   * @author: BMThang(17/01/2022)
   */
  const deadLineSetting = computed(() => {
    if (deadlineRaw.value.length) {
      const deadlineValueString = deadlineRaw.value[0].SettingValue;
      const deadlineValueJson = JSON.parse(deadlineValueString);
      const deadlineValue = deadlineValueJson.OptionValue || 0;
      return deadlineValue;
    }
    return 0;
  })

  /**
   * Danh sách người đc người hiện tại ủy quyền
   */
  const authorizedUsers = computed(() => {
    // Format lại người đc ủy quyền
    const authorizedUserList = [...authorizedPersionRaw.value].map(user => {
      return {
        UserID: user.UserID,
        UserName: user.UserName,
        MISAID: user.MISAID,
        MISAIDEmail: user.MISAIDEmail,
        DateOfBirth: user.DateOfBirth,
        FirstName: user.FirstName,
        LastName: user.LastName,
        Email: user.Email,
        FullName: user.FullName,
        Mobile: user.Mobile,
        WorkPhone: user.WorkPhone,
        OrganizationUnitID: user.OrganizationUnitID,
        OrganizationUnitName: user.OrganizationUnitName,
        OrganizationID: user.OrganizationID,
        OrganizationName: user.OrganizationName,
        Status: user.Status,
        JobTitleID: user.JobTitleID,
        JobTitleName: user.JobTitleName,
        RoleType: user.RoleType,
        IsAdmin: user.IsAdmin,
      }
    });

    if (authorizedUserList.length) {
      // Thêm người dùng hiện tại vào danh sách
      authorizedUserList.unshift({ ...store.state.context.User, Created: null, Modified: null });
    }
    return authorizedUserList;
  })

  /**
   * Người dùng hiện tại thông qua ủy quyền.
   * Nếu đc người khác ủy quyền trả về người ủy quyền to nhất.
   * Nếu ko ai quỷ quyền trả về bản thân minh
   * @author: BMThang(06/02/2023)
   */
  const currentUserByAuthorization = computed(() => {
    if (authorizedPersionRaw.value.length) {
      return authorizedPersionRaw.value[0];
    }

    return store.state.context.User;
  })

  /**
   * Vai trò người dùng
   * @author: BMThang(03/01/2022)
   */
  const configUserRole = computed(() => {
    const result = [];
    // Là người tạo
    if (
      authorizedUsers.value.findIndex(user => user.UserID === taskData.value.CreatorID) >= 0 ||
      taskData.value.CreatorID === store.state.context.User.UserID
    ) {
      result.push(window.TaskRolesRole.Assigner);
    }
    // Là người giao
    if (
      taskData.value.Assigner.some(e =>
        e.UserID === store.state.context.User.UserID ||
        authorizedUsers.value.findIndex(user => user.UserID === e.UserID) >= 0
      )
    ) {
      result.push(window.TaskRolesRole.Assigner);
    }
    // Là người phụ trách quyền giống người giao việc
    if (
      taskData.value.Supervisor.some(e =>
        e.UserID === store.state.context.User.UserID ||
        authorizedUsers.value.findIndex(user => user.UserID === e.UserID) >= 0
      )
    ) {
      result.push(window.TaskRolesRole.Supervisor);
    }
    // Là người chủ trì
    if (
      taskData.value.Executor.some(e =>
        e.UserID === store.state.context.User.UserID ||
        authorizedUsers.value.findIndex(user => user.UserID === e.UserID) >= 0
      )
    ) {
      result.push(window.TaskRolesRole.Executor);
    }
    // Là người phối hợp
    if (
      taskData.value.Combinations.some(e =>
        e.UserID === store.state.context.User.UserID ||
        authorizedUsers.value.findIndex(user => user.UserID === e.UserID) >= 0
      )
    ) {
      result.push(window.TaskRolesRole.Combinations);
    }
    // Là người liên quan
    if (
      taskData.value.PeopleInvolveds.some(e =>
        e.UserID === store.state.context.User.UserID ||
        authorizedUsers.value.findIndex(user => user.UserID === e.UserID) >= 0
      )
    ) {
      result.push(window.TaskRolesRole.PeopleInvolveds);
    }
    // Nếu ko thuộc trường hợp nào bên trên thì mặc định cho là người phối hợp
    if (result.length === 0) {
      result.push(window.TaskRolesRole.PeopleInvolveds);

      // Thêm vai trò cấp trên vào
      result.push(window.TaskRolesRole.Superior);
    }
    return result;
  })

  /**
   * Tình trạng công việc
   * @author: BMThang(03/01/2022)
   */
  const configTasktatusDetail = computed(() => {
    if (taskData.value.Status === TaskStatus.Draft) {
      return window.TaskRolesStatusDetail.Draft;
    }
    if (!taskData.value.EndDate) {
      return window.TaskRolesStatusDetail.Safe;
    }
    if (taskData.value.EndDate) {
      const endDate = new Date(taskData.value.EndDate);
      const currentDate = new Date();
      if (currentDate > endDate) {
        const maxEndDate = endDate.setDate(endDate.getDate() + deadLineSetting.value);
        if (currentDate > maxEndDate && taskData.value.Status === TaskStatus.InProcess) {
          return window.TaskRolesStatusDetail.NotDone;
        }
        return window.TaskRolesStatusDetail.OutOfTime;
      }
    }
    return window.TaskRolesStatusDetail.Safe;
  })

  /**
   * Trạng thái công việc
   * @author: BMThang(03/01/2022)
   */
  const configTaskStatus = computed(() => {
    if (taskData.value.Status === TaskStatus.Draft) {
      return window.TaskRolesStatus.Draft;
    }
    if (taskData.value.Status === TaskStatus.InProcess) {
      if (taskApprovals.value?.length) {
        if (taskApprovals.value[0].Status === TaskApprovalEnum.WaitApproval) {
          return window.TaskRolesStatus.WaitingApproval;
        }
        if (taskApprovals.value[0].Status === TaskApprovalEnum.Approved) {
          return window.TaskRolesStatus.Approved;
        }
      }
      return window.TaskRolesStatus.InProcess;
    }
    return window.TaskRolesStatus.Approved;
  })

  /**
   * Config controls
   * @author: BMThang(03/01/2022)
   */
  const configControls = computed(() => {
    if (!window.TaskRoles) {
      return {};
    }

    const configStatus = configTaskStatus.value;
    const configStatusDetail = configTasktatusDetail.value;
    const configRole = configUserRole.value;

    const result = [];
    configRole.forEach(role => {
      const config = window.TaskRoles.find(configControl =>
        configControl.Status === configStatus &&
        configControl.StatusDetail === configStatusDetail &&
        configControl.Role === role
      );
      if (config) {
        result.push(config.Value);
      }
    })

    const resultObject = {};

    if (result.length) {
      for (const key in result[0]) {
        const values = result.map(v => v[key]);
        resultObject[key] = Math.min(...values);
      }
    }

    return resultObject;
  })

  /**
   * Xử lý hiển thị thông báo khi có lỗi lưu công việc
   * @param {object} err http response
   * @author: BMThang(26/12/2022)
   */
  const handleSaveTaskFail = (err) => {
    if (err.response.status === MSEnum.HttpStatusCodes.BusinessError) {
      var errData = "";
      if (err.response.data) {
        errData = typeof (err.response.data) == 'string' ? JSON.parse(err.response.data) : err.response.data;
        if (errData.Error === MSEnum.ServerErrorCodes.Validate) {
          if (errData.Data.some(e => e.Code === MSEnum.ValidateCodes.EditVersion)) {
            toast.error(t('i18nCommon.EditVersionErrorMsg'));
            return;
          }
        }
      }

    }
    toast.error(t('i18nCommon.ErrorOccurMsg'));
  }

  /**
   * Đổi trạng thái tất cả công việc (cả cha và con) về giá trị mong muốn
   * @param {object} currentTask Công việc cần đổi
   * @param {number} taskStatus Trạng thái mong muốn
   * @author: BMThang(30/01/2023)
   */
  const changeAllTaskStatus = (taskStatus, currentTask = taskData.value) => {
    let currentTasks = [currentTask];
    while (currentTasks.length > 0) {
      const newCurrentTasks = [];
      currentTasks.forEach(t => {
        // Nếu đổi trạng thái và State của task ko phải là Insert thì đổi State thành Update
        if (t.Status !== taskStatus && t.State !== MSEnum.ModelState.Insert) {
          t.State = MSEnum.ModelState.Update;
        }
        t.Status = taskStatus;
        if (Array.isArray(t.ChildrenTask)) {
          newCurrentTasks.push(...t.ChildrenTask);
        }
      });
      currentTasks = newCurrentTasks;
    }
  }

  /**
   * Kiểm tra các trường thông tin lặp lại có thay đổi không
   */
  const checkChangeTaskRepeat = async (task, oldTask) => {
    let answer = false;

    let newEndDate = task.EndDate.IsSelectEndTime ?  _.cloneDeep(task.EndDate) : _.cloneDeep(task.EndDate.getDateOnly());
    let oldEndDate = oldTask.IsSelectEndTime ? _.cloneDeep(new Date(oldTask.EndDate)) : _.cloneDeep(new Date(oldTask.EndDate).getDateOnly());
    if (newEndDate.toISOString() != oldEndDate.toISOString()) {
      answer = null;
      let textButton = [
        {
          Text: 'Hủy',
          Class: 'secondary',
          HasBorder: true,
          Answer: null
        },
        {
          Text: 'Chỉ bản ghi hiện tại',
          Class: 'secondary',
          HasBorder: true,
          Answer: false
        },
        {
          Text: 'Thiết lập',
          Class: 'primary',
          Answer: true
        }
      ]
      answer = await showConfirmCustom('Bạn đã thay đổi hạn hoàn thành của công việc lặp định kỳ. Nếu muốn cập nhật tất cả các công việc sau cần thiết lập lặp lại công việc.',
        'Lưu công việc',
        textButton);
      if (answer != null) {
        task.IsRedirectTaskRepeat = answer;
      }

      return answer;
    }

    let isChange = false;
    let props = ['TaskName', 'Priority', 'DocumentCode', 'DocumentDate', 'DocumentSigner', 'TaskType', 'Description', 'Executor', 'Assigner', 'Combinations', 'PeopleInvolveds', 'Attachments', 'Proportion', 'Supervisor'];
    for (let i = 0; i < props.length; i++) {
      if (JSON.stringify(task[props[i]]) != JSON.stringify(oldTask[props[i]])) {
        isChange = true;
        break;
      }
    }

    if (isChange) {
      answer = null;
      let textButton = [
        {
          Text: 'Hủy',
          Class: 'secondary',
          HasBorder: true,
          Answer: null
        },
        {
          Text: 'Chỉ bản ghi hiện tại',
          Class: 'secondary',
          HasBorder: true,
          Answer: false
        },
        {
          Text: 'Áp dụng',
          Class: 'primary',
          Answer: true
        }
      ]
      answer = await showConfirmCustom('Đây là công việc định kỳ. Bạn có muốn những thông tin vừa thay đổi được áp dụng cho các công việc sau không?',
        'Lưu công việc',
        textButton);
      if (answer == null) {
        return answer;
      } else {
        task.IsUpdateTaskRepeat = answer;
      }
    }

    return answer;
  }

  /**
   * Hàm xử lý khi lưu task
   * @param {string} action hành động khi nhấn btn
   * @param {function} callbackSuccess hàm thực hiện khi thành công
   * @param {function} callbackFail hàm thực hiện khi thất bại
   * @param {object} inputTask dữ liệu công việc truyền vào khi ko muốn lưu công việc từ taskData
   * @author: BMThang(27/12/2022)
   */
  const handleSaveTask = async (action, callbackSuccess = () => { }, callbackFail = () => { }, inputTask = null) => {
    try {
      // Nếu truyền inputTask thì lưu inputTask
      // Nếu ko thì mặc định lưu taskData
      const clonedTaskData = JSON.parse(JSON.stringify(inputTask ? inputTask : taskData.value));

      // Lấy trạng thái và thông báo khi lưu thành công của công việc
      let taskStatus = taskData.value.Status;
      if (action === TaskAction.Send) {
        // Nếu công việc chưa đc lưu hoặc đang ở trạng thái nháp => Gửi lần đầu => lưu ngày gửi
        // if (!taskData.value.TaskID || taskData.value.Status === TaskStatus.Draft) {
        //   clonedTaskData.SendTaskDate = new Date();
        // }
        if (taskData.value.Status === TaskStatus.Draft) {
          taskStatus = TaskStatus.InProcess;
        }
      }

      formatTaskBeforeSave(clonedTaskData, oldTaskData.value, taskStatus);

      // Nếu task đã có ID thì xét State = Update
      // Chỉ xét task cha
      if (clonedTaskData.TaskID) {
        clonedTaskData.State = MSEnum.ModelState.Update;
      }

      const res = await taskAPI.saveTask([clonedTaskData]);
      const resJson = JSON.parse(res);

      // param 1: Danh sách ID dạng { Data: [ { TaskID: 123 } ] }
      // param 2: Trạng thái của công việc đc lưu lên db
      callbackSuccess(resJson, taskStatus);
    } catch (err) {
      console.log(err)
      callbackFail(err);
    }
  };

  /**
   * Xét giá trị mặc định cho công việc
   * @author: BMThang(27/12/2022)
   */
  const setDefaultValue = () => {
    if (priorityOptions.value.length) {
      var selectedPriority = priorityOptions.value.filter(item => {
        return item.IsDefault;
      });
      taskData.value.Priority = selectedPriority.length ? [selectedPriority[0]] : priorityOptions.value.length ? [priorityOptions.value[0]] : [];
      oldTaskData.value.Priority = JSON.parse(JSON.stringify(taskData.value.Priority));
    }
    if (taskTypeOptions.value.length) {
      var selectedTaskType = taskTypeOptions.value.filter(item => {
        return item.IsDefault;
      });
      taskData.value.TaskType = selectedTaskType.length ? [selectedTaskType[0]] : taskTypeOptions.value.length ? [taskTypeOptions.value[0]] : [];
      oldTaskData.value.TaskType = JSON.parse(JSON.stringify(taskData.value.TaskType));
    }
  }

  /**
   * Lấy các option
   * @author: BMThang(27/12/2022)
   */
  const getOptions = async () => {
    const [resPriority, resTaskType, resDeadline, resAuthorizedPeople] = await Promise.all([
      getSettingOptions(settingEnum.PriorityLevel),
      getSettingOptions(settingEnum.TaskType),
      getSettingOptions(settingEnum.Deadline),
      authorizedPersonAPI.getAuthorizedPersonForMe(),
    ]);

    taskTypeOptionsRaw.value = resTaskType;
    priorityOptionsRaw.value = resPriority;
    deadlineRaw.value = resDeadline;
    authorizedPersionRaw.value = resAuthorizedPeople;
    if (taskData.value.Priority?.length == 0) {
      // Xét giá trị mặc định cho công việc
      setDefaultValue();
    }
  }

  if (isGetSetting) {
    getOptions();
  } else {
    if (taskData.value.Priority?.length == 0) {
      // Xét giá trị mặc định cho công việc
      setDefaultValue();
    }
  }

  return {
    priorityOptions,
    taskTypeOptions,
    tabListOwner,
    tabListCombination,
    tabListPeopleInvolved,
    tabListSupervisor,
    taskData,
    oldTaskData,
    configControls,
    taskApprovals,
    authorizedUsers,
    deadLineSetting,
    configTasktatusDetail,
    configUserRole,
    currentUserByAuthorization,
    authorizedPersionRaw,
    changeAllTaskStatus,
    handleSaveTask,
    handleSaveTaskFail,
    getOptions,
    checkChangeTaskRepeat,
  }
}